import { useState } from 'react';
import { Eye, Edit3 } from 'lucide-react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export default function MarkdownEditor({ value, onChange, placeholder, minHeight = '200px' }: MarkdownEditorProps) {
  const [isPreview, setIsPreview] = useState(false);

  // Simple markdown-to-HTML converter (basic implementation)
  function renderMarkdown(markdown: string): string {
    return markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\_\_(.*?)\_\_/g, '<strong class="font-semibold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/\_(.*?)\_/g, '<em class="italic">$1</em>')
      // Links
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-accent-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
      // Line breaks
      .replace(/\n\n/g, '</p><p class="mb-4">')
      .replace(/\n/g, '<br>');
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
        <div className="text-sm text-ink-muted">
          {isPreview ? 'Preview' : 'Edit'} • Markdown supported
        </div>
        <button
          onClick={() => setIsPreview(!isPreview)}
          className="flex items-center gap-2 px-3 py-1 text-sm text-ink-secondary hover:bg-gray-200 rounded transition-colors"
        >
          {isPreview ? (
            <>
              <Edit3 className="w-4 h-4" />
              Edit
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              Preview
            </>
          )}
        </button>
      </div>

      {/* Content Area */}
      {isPreview ? (
        <div
          className="p-4 prose max-w-none"
          style={{ minHeight }}
          dangerouslySetInnerHTML={{ __html: `<p class="mb-4">${renderMarkdown(value)}</p>` }}
        />
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Write your section here... Markdown is supported:\n\n# Heading 1\n## Heading 2\n**bold** *italic*\n[link text](url)'}
          className="w-full p-4 border-0 focus:outline-none focus:ring-0 resize-none font-mono text-sm"
          style={{ minHeight }}
        />
      )}

      {/* Character count */}
      <div className="bg-gray-50 border-t border-gray-300 px-4 py-2 text-xs text-ink-muted text-right">
        {value.length.toLocaleString()} characters
      </div>
    </div>
  );
}
